import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, ArrowRight } from "lucide-react";
import { useLang } from "../i18n";
import { getPost, BLOG_POSTS, postUrl } from "../data/blog";
import { getService } from "../data/site";
import Seo, { breadcrumbLd } from "../components/Seo";
import { Reveal, Kicker } from "../components/Reveal";

function renderInline(text, prefix, keyBase) {
  const parts = [];
  let rest = text;
  let k = 0;
  while (rest.length) {
    const m = rest.match(/\[([^\]]+)\]\(([^)]+)\)/);
    const b = rest.match(/\*\*([^*]+)\*\*/);
    let first = null;
    if (m && (!b || m.index <= b.index)) first = { m, type: "link" };
    else if (b) first = { m: b, type: "bold" };
    if (!first) {
      parts.push(rest);
      break;
    }
    if (first.m.index > 0) parts.push(rest.slice(0, first.m.index));
    if (first.type === "link") {
      let href = first.m[2];
      if (prefix === "/ca" && href.startsWith("/") && !href.startsWith("/ca")) href = `/ca${href}`;
      parts.push(
        <Link key={`${keyBase}-${k++}`} to={href} className="font-semibold text-[#63E51D] underline decoration-[#63E51D]/40 underline-offset-4 hover:text-[#8BFF32]">
          {first.m[1]}
        </Link>
      );
    } else {
      parts.push(<strong key={`${keyBase}-${k++}`} className="font-semibold text-white">{renderInline(first.m[1], prefix, `${keyBase}b${k}`)}</strong>);
    }
    rest = rest.slice(first.m.index + first.m[0].length);
  }
  return parts;
}

function Markdown({ text, prefix }) {
  const lines = text.split("\n");
  const blocks = [];
  let list = null;
  let table = null;
  let key = 0;

  const flush = () => {
    if (list) {
      blocks.push(
        <ul key={key++} className="my-4 space-y-2">
          {list.map((li, i) => (
            <li key={i} className="flex items-start gap-3 text-base leading-relaxed text-zinc-300">
              <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#63E51D]" />
              <span>{renderInline(li, prefix, `li${i}`)}</span>
            </li>
          ))}
        </ul>
      );
      list = null;
    }
    if (table) {
      blocks.push(
        <div key={key++} className="my-6 overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#171717]">
                {table.head.map((h, i) => (
                  <th key={i} className="px-4 py-3 text-left font-display text-base font-bold uppercase tracking-wide text-[#8BFF32]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((r, i) => (
                <tr key={i} className="border-t border-white/5">
                  {r.map((c, j) => (
                    <td key={j} className="px-4 py-3 text-zinc-300">{c}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      table = null;
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (line.startsWith("|")) {
      const cells = line.split("|").slice(1, -1).map((c) => c.trim());
      if (cells.every((c) => /^:?-+:?$/.test(c))) continue;
      if (!table) table = { head: null, rows: [] };
      if (!table.head) table.head = cells;
      else table.rows.push(cells);
      continue;
    }
    if (line.startsWith("- ")) {
      if (!list) list = [];
      list.push(line.slice(2));
      continue;
    }
    flush();
    if (!line.trim()) continue;
    if (line.startsWith("### ")) blocks.push(<h3 key={key++} className="mt-8 font-display text-xl font-bold uppercase tracking-wide text-white sm:text-2xl">{line.slice(4)}</h3>);
    else if (line.startsWith("## ")) blocks.push(<h2 key={key++} className="mt-10 font-display text-2xl font-bold uppercase tracking-tight text-[#8BFF32] sm:text-3xl">{line.slice(3)}</h2>);
    else if (line.startsWith("# ")) continue;
    else blocks.push(<p key={key++} className="mt-4 text-base leading-relaxed text-zinc-300">{renderInline(line, prefix, `p${key}`)}</p>);
  }
  flush();
  return <>{blocks}</>;
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const prefix = lang === "ca" ? "/ca" : "";
  const post = getPost(slug);
  const [md, setMd] = useState(null);

  useEffect(() => {
    setMd(null);
    fetch(`/blog/${slug}.${lang}.md`)
      .then((r) => (r.ok ? r.text() : Promise.reject()))
      .catch(() => fetch(`/blog/${slug}.es.md`).then((r) => r.text()))
      .then(setMd)
      .catch(() => setMd("# Error"));
  }, [slug, lang]);

  if (!post) return null;
  const d = post[lang] || post.es;
  const relatedServices = (post.services || []).map(getService).filter(Boolean);
  const morePosts = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <div data-testid={`blog-post-${slug}`} className="pt-32">
      <Seo
        title={`${d.title} | EcoGlow Detailing`}
        desc={d.excerpt}
        path={`/blog/${slug}`}
        image={post.img}
        jsonLd={breadcrumbLd([
          { name: "EcoGlow Detailing", path: prefix || "/" },
          { name: "Blog", path: `${prefix}/blog` },
          { name: d.title, path: `${prefix}/blog/${slug}` },
        ])}
      />
      <article className="mx-auto max-w-4xl px-4 sm:px-6">
        <Link to={`${prefix}/blog`} data-testid="blog-back-link" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-[#8BFF32]">
          <ArrowLeft className="h-4 w-4" /> {t("blog.back")}
        </Link>
        <Reveal>
          <div className="mt-6 flex items-center gap-3">
            <Kicker>Blog</Kicker>
            <span className="flex items-center gap-1.5 font-mono2 text-[11px] uppercase tracking-widest text-zinc-500">
              <Clock className="h-3.5 w-3.5" /> {post.minutes} {t("blog.min")}
            </span>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <h1 className="mt-4 font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-white sm:text-5xl">
            {d.title}
          </h1>
        </Reveal>
        <Reveal delay={0.2}>
          <img src={post.img} alt={d.title} className="mt-10 aspect-[16/8] w-full rounded-2xl border border-white/5 object-cover" />
        </Reveal>
        <div data-testid="blog-post-content" className="mt-8">
          {md === null ? <p className="text-zinc-500">…</p> : <Markdown text={md} prefix={prefix} />}
        </div>

        {relatedServices.length > 0 && (
          <div className="mt-14 rounded-2xl border border-[#63E51D]/25 bg-[#101508] p-7">
            <h2 className="font-mono2 text-xs uppercase tracking-[0.25em] text-[#8BFF32]">{t("blog.related")}</h2>
            <div className="mt-4 flex flex-wrap gap-3">
              {relatedServices.map((s) => (
                <Link
                  key={s.slug}
                  to={`${prefix}/servicios/${s.slug}`}
                  data-testid={`blog-related-service-${s.slug}`}
                  className="rounded-full border border-[#63E51D]/40 px-5 py-2.5 text-sm font-semibold text-[#8BFF32] transition-all duration-300 hover:bg-[#63E51D] hover:text-black"
                >
                  {s[lang]?.name || s.es.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12">
        <Reveal>
          <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl">{t("blog.more")}</h2>
        </Reveal>
        <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
          {morePosts.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.08}>
              <Link
                to={postUrl(p.slug, lang)}
                data-testid={`blog-more-${p.slug}`}
                className="group flex h-full flex-col overflow-hidden rounded-xl border border-white/5 bg-[#121212] transition-all duration-300 hover:-translate-y-1 hover:border-[#63E51D]/50"
              >
                <img src={p.img} alt={p[lang].title} loading="lazy" className="aspect-[16/9] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="flex flex-1 flex-col p-5">
                  <h3 className="font-display text-lg font-bold uppercase leading-tight tracking-wide text-white group-hover:text-[#8BFF32]">
                    {p[lang].title}
                  </h3>
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#63E51D]">
                    {t("blog.read")} <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
